import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src'
import { ProfileEntity } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { GbBaseApiService } from 'libs/shared/data-access-core/src/lib/+state/gb-base-api.service'
import { Observable } from 'rxjs'
import {
  AuthenticationEntity,
  ChangePasswordPayload,
  RegisterResponse,
  ResendSmsVerificationResponse,
  forgotPasswordPayload,
  RegisterPayload,
  RegisterVolunteerPayload,
  verifyForgotPasswordPayload,
  volunteerLoginPayload,
  VerifyForgotPasswordResponse,
} from './auth.models'

@Injectable({
  providedIn: 'root',
})
export class AuthApiService extends GbBaseApiService {
  get isVolunteer(): boolean {
    return this.gbFrontCookieService.isVolunteer()
  }

  constructor(
    protected http: HttpClient,
    @Inject(APP_CONFIG) protected appConfig: any,
    private gbFrontCookieService: GbFrontCookieService,
  ) {
    super(http, appConfig)
  }

  /**
   *  should get from the API the login state
   * @param identity email or phone number which is the user login
   * @param password password
   * @returns Observable<AuthenticationState>
   */
  appLogin(urlName: string, identity: string, password: string): Observable<AuthenticationEntity> {
    const url = `auctions/${urlName}/login/`

    const payload = {
      identity: identity,
      password: password,
    }

    return this.post<AuthenticationEntity>(url, payload)
  }

  /**
   *
   * @param payload the registration fields, they are invite first name, last name, identity etc
   * @returns Observable<RegisterPayload>
   */
  register(urlName: string, payload: RegisterPayload | RegisterVolunteerPayload): Observable<RegisterResponse> {
    const url = `auctions/${urlName}/register/`
    return this.post<RegisterResponse>(url, payload)
  }

  /**
   * check with the api the verification code
   * @param payload {identity: '', verification_code: '123456'}
   * @returns Observable<AuthenticationState>
   */
  verify(urlName: string, payload: {}): Observable<AuthenticationEntity> {
    const url = `auctions/${urlName}/verify/`
    return this.post<AuthenticationEntity>(url, payload)
  }

  /**
   * check with the api the verification code
   * @param token
   * @returns Observable<AuthenticationState>
   */
  verifyToken(urlName: string, token: string): Observable<AuthenticationEntity> {
    const payload = { verification_token: token }
    const url = `auctions/${urlName}/verify/`
    return this.post<AuthenticationEntity>(url, payload)
  }

  /**
   * Verifies the phone number
   * @param token
   * @returns Observable<void>
   */
  verifyNotification(urlName: string, token: string): Observable<undefined> {
    const payload = { verification_token: token }
    const url = `auctions/${urlName}/verify-notification/`
    return this.post<undefined>(url, payload)
  }

  /**
   *  should get from the API the login state
   * @param password password
   * @returns Observable<AuthenticationState>
   */
  appVolunteerLogin(urlName: string, payload: volunteerLoginPayload): Observable<AuthenticationEntity> {
    const url = `auctions/${urlName}/login/volunteer/`
    return this.post<AuthenticationEntity>(url, payload)
  }

  refreshAuthToken(urlName: string): Observable<AuthenticationEntity> {
    const url = `auctions/${urlName}/login/${this.appConfig.refreshTokenUrl}/`
    return this.post<AuthenticationEntity>(url)
  }

  /**
   *  should get from the API the login state
   * @param access_token google facebbok or any other new oauth login token
   * @param provider string <google-oauth2 | facebook>
   * @returns Observable<AuthenticationState>
   */
  appSocialLogin(urlName: string, accessToken: string, provider: string): Observable<AuthenticationEntity> {
    const url = `auctions/${urlName}/login/social/`

    const payload = {
      access_token: accessToken,
      provider: provider,
    }

    return this.post<AuthenticationEntity>(url, payload)
  }

  forgotPassword(urlName: string, payload: forgotPasswordPayload) {
    const url = `auctions/${urlName}/forgot_password/`
    return this.post<void>(url, payload)
  }

  verifyForgotPassword(
    urlName: string,
    payload: verifyForgotPasswordPayload,
  ): Observable<VerifyForgotPasswordResponse> {
    const url = `auctions/${urlName}/verify_forgot_password/`
    return this.post<VerifyForgotPasswordResponse>(url, payload)
  }

  changePassword(urlName: string, payload: ChangePasswordPayload) {
    const url = `auctions/${urlName}/me/reset_password/`
    return this.post<ProfileEntity>(url, payload)
  }

  resendPhoneNumberVerification(urlName: string, profileUuid: string): Observable<ResendSmsVerificationResponse> {
    const payload = {
      profile: profileUuid,
    }

    const url = `auctions/${urlName}/resend-phone-number-verification/`
    return this.post<ResendSmsVerificationResponse>(url, payload)
  }

  /**
   * @param urlName
   * @returns currently logged in ProfileEntity based on the attached auth token
   */

  getCurrentAuthProfile(urlName: string): Observable<ProfileEntity> {
    let url = `auctions/${urlName}/me/profile/`
    if (this.isVolunteer) url = `auctions/${urlName}/volunteer/`
    return this.get<ProfileEntity>(url)
  }

  /**
   * Log the user out which will invalidate the token and refresh token on the API
   */
  logout(): Observable<void> {
    return this.post<void>('logout/')
  }
}
